// shared/src/resolvers/llm-number-coerce.ts
// 2026-06-11 — translate LLM-emitted NUMBER fields that arrive as decorated
// STRINGS to plain numbers at the boundary, instead of hard-failing the model
// against a rigid z.number().
//
// Output-side sibling of llm-string-coerce.ts / llm-enum-coerce.ts. Same standing
// rule: NEVER hard-fail a whole brief (~3.5 min / ~$0.16) because the model
// returned a numerically-valid value in a slightly-decorated string shape. The
// dominant un-handled rigidity class across the prompt OutputSchemas (audit
// 2026-06-11, docs/investigations/llm-schema-rigidity-audit-*) was a z.number()
// field receiving "65", "+2.0", "3.5%", "1,000", or "$1,200" — a string that
// carries the exact number the prompt asked for, wrapped in formatting the model
// added on its own.
//
// CONSERVATIVE BY DESIGN (per [[feedback_no_fallbacks]]): we only strip
// UNAMBIGUOUS numeric decoration — a single leading "+", a leading "$", a single
// trailing "%", and thousands-commas — and only return a number when what remains
// is a CLEAN numeric literal. Anything genuinely ambiguous ("7 days", "3 to 5",
// "N/A", "~2") passes through UNCHANGED so the wrapping z.number() (and its
// .min/.max/.int range checks) still fail-loud. We never parseFloat-and-truncate
// ("3.5 to 5.0" must NOT silently become 3.5), never invent a value, never swallow
// null/undefined. Range/sign/int constraints stay material — only the string
// envelope is recovered.

import { z } from 'zod';

// A clean optionally-signed integer or decimal, nothing else. Tested AFTER
// decoration-stripping. Note: no exponent form (a bare "1e5" from an LLM is
// suspicious enough to fail-loud) and no leading/trailing junk.
const CLEAN_NUMERIC = /^-?\d+(?:\.\d+)?$/;

/**
 * Coerce one LLM numeric value to a number (or pass it through for the wrapping
 * z.number() to fail-loud on). Numbers pass unchanged. A string is stripped of
 * unambiguous decoration (leading `+`/`$`, trailing `%`, thousands-commas,
 * surrounding whitespace/quotes); if the remainder is a clean numeric literal it
 * becomes a Number, otherwise it passes through unchanged. null/undefined/objects
 * pass through unchanged → the wrapping z.number() fail-louds.
 */
export function coerceLlmNumber(v: unknown): unknown {
  if (typeof v === 'number') return v;
  if (typeof v !== 'string') return v; // null/undefined/object/bool → fail-loud

  let s = v.trim();
  if (s === '') return v;
  // strip a single pair of surrounding quotes the model occasionally adds
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1).trim();
  }
  s = s
    .replace(/^\+/, '')              // leading "+2.0" → "2.0"
    .replace(/^\$/, '')              // leading "$1200" → "1200"
    .replace(/%$/, '')               // trailing "3.5%" → "3.5"
    .replace(/,(?=\d{3}(?:\D|$))/g, ''); // thousands-commas "1,000,000" → "1000000"

  if (CLEAN_NUMERIC.test(s)) return Number(s);
  return v; // ambiguous ("7 days", "3 to 5", "~2", "N/A") → z.number() fail-louds
}

/**
 * Drop-in replacement for `z.number()` (and any number subtype) in LLM-output
 * schemas. Accepts the number OR a decorated-string form and yields the inner
 * schema's number. Chain range checks via the `inner` arg so they stay material:
 *   llmNumber()                              // bare number
 *   llmNumber(z.number().min(0).max(100))    // bounded
 *   llmNumber(z.number().int().positive())   // integer
 *   llmNumber(z.number().min(0).max(1)).nullable()  // nullable bounded
 * z.infer is the inner schema's output (number), so typed consumers are unchanged.
 */
export function llmNumber<T extends z.ZodTypeAny = z.ZodNumber>(inner?: T) {
  return z.preprocess(coerceLlmNumber, (inner ?? z.number()) as T);
}
